// IMPORTS

// Angular Core Modules
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Component, OnInit, OnDestroy, ViewChild, ElementRef, Output, EventEmitter, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

// Components
import { GenericErrorModal } from 'modules/teacher/components/generic-error-modal/generic-error-modal.component';

// Internal Libraries
import { ContentHelper } from 'libs-content-helper';

// Local Modules
import translations from './../../../../assets/i18n/en';

// Services
import { ApiGatewayHelperService } from '../../../app/services/apigateway-helper.service';
import { AppHelperService } from 'modules/app/services/app-helper.service';
import { AssignmentViewService, AssignmentData, FlattenProductItem, AssignmentDataParams, EditAssignmentDataParams } from 'modules/teacher/services/assignment-view.service';
import { HtmlMetaManagerService } from '../../../app/services/html-meta-manager.service';
import { LoaderService } from '../../../app/services/loader.service';
import { MessageService, MessageServiceData } from 'modules/shared-all/services/message.service';
import { ProductTocService } from 'modules/shared-postlogin/services/product-toc.service';
import { ProductViewHelperService } from 'modules/shared-postlogin/services/product-view-helper.service';
import { ContentAccessHelperService } from 'modules/shared-postlogin/services/content-access-helper.service';
import { OwlDateTimeComponent } from '@danielmoncada/angular-datetime-picker';
import { ContentAccessService } from 'modules/teacher/services/content-access.service';

// Third Party Libraries
import { nanoid } from 'nanoid';
import { Subscription } from 'rxjs';
import { EventService } from 'modules/shared-postlogin/services/event.service';
import { AuthService } from 'modules/app/services/auth.service';

declare var $: any;

// Interfaces
interface RulesUnitMap {
  [key: string]: boolean | number;
}

interface AssignedLearners {
  'extUserId': string;
}

@Component({
    selector: 'assignment-detail',
    templateUrl: './assignment-detail.template.html',
    styleUrls: ['./assignment-detail.style.scss'],
    standalone: false
})

export class AssignmentDetail implements OnInit, OnDestroy {

  // Private variables
  private classId: string;
  private paramsSubscription: Subscription;
  private productCode: string;
  private spaceId: string;
  private assignmentId: string;
  private subscriptions = new Subscription();
  private isMosaicTypeComponent: boolean = false;
  private isSummaryBackBtnClicked: boolean = false;
  private isLockRuleUserScope: boolean;
  private componentRuleLimitReached: boolean = false;
  private rulesDocSizeLimitExceeded: boolean = false;
  private ruleParamsForRetry: any;
  private lastActiveElement: HTMLElement | null = null;

  // Public variables
  public assignmentData: AssignmentData;
  public classEndDate: Date;
  public classStartDate: Date;
  public classTitle: string;
  /* Contains not assigned items of current component */
  public currentComponentItemsNotAssigned: FlattenProductItem;
  public isCancelBtnClicked: boolean = false;
  /* Tells whether items of this component is completely assigned or not */
  public isCurrentComponentAllItemsAssigned: boolean = false;
  public isTestTypeComponent: boolean = false;
  /* Contains all the assigned assignments items within bundle */
  public itemsAlreadyAssignedInClass: FlattenProductItem;
  public minAssignmentStartDate: Date = new Date();
  public assignmentStartDateMaxValue: Date = new Date();
  public assignmentDueDateMinValue: Date = new Date();
  public productBundleDetails: any;
  public bundleProductMeta: any;
  public redirectData: MessageServiceData;
  public showLoader: boolean = false;
  public showRulesWarning: boolean = false;
  public showLockRulesDetails: boolean = false;
  public unitConflictsMap: RulesUnitMap = {};
  public lessonConflictsMap: FlattenProductItem = {};
  public SECTION_TYPE = {
    DETAILS: 'DETAILS',
    STUDENT: 'STUDENT',
    SUMMARY: 'SUMMARY',
    CONTENT: 'CONTENT'
  };
  public updatedSection: 'DETAILS' | 'STUDENT' | 'CONTENT';
  public currentSectionType: string = this.SECTION_TYPE.DETAILS;
  public classLearners: any = [];
  public noOfSelectedLearners: number = 0;
  public currentDatetime = new Date().getTime();
  public isAssignmentEditMode: boolean = false;
  public isUpdateButtonDisabled: boolean = true;
  public currentAssignmentData: any = {};
  public existingAssignmentData: any = {};
  public translationTexts: any = {};
  /** Flag to show/hide tooltip when edit student button is disabled */
  public showTooltipForNoEditStud: boolean = false;
  private showUpdateBannerTimeout: NodeJS.Timer;
  public isStudentSelectionUpdated: boolean = false;
  public isStartDateChanged: boolean = false;
  public isDueDateChanged: boolean = false;
  public isTitleChanged: boolean = false;
  public isAssignmentConentUpdated: boolean = false;
  public canCreateAnotherAssignment: boolean = true;
  public fromEditWarningModal: boolean = false;
  public isLockContentCheckboxUpdated: boolean = false;
  /** Flag to show/hide lock content checkbox */
  public hideLockContentCheckbox: boolean = false;
  
  // Constant variables
  public imageBasePath: string = IMAGES_BASEPATH;
  public ruleLimitInComponent: number = parseInt(RULE_LIMIT_IN_COMPONENT);

  // Inputs from parent component
  @Input() ebookAssignmentFlowParams: any;
  @Input() itemsAlreadyAssigned: FlattenProductItem;
  @Input() noOfCheckedItems: number;
  @Input() productTocData: any;
  @Input() tocArray: any[];
  @Input() tocFolderDepth: number;
  @Input() isEditMode: boolean;

  // Event Emitters to parent component
  @Output() noOfCheckedItemsChange = new EventEmitter<number>();
  @Output() tocArrayChange = new EventEmitter<any[]>();
  @Output() showAssignmentLimitWarningModal = new EventEmitter<boolean>();

  // DOM References
  @ViewChild('exitWarningModalButton') exitWarningModalButton: ElementRef;
  @ViewChild('closeExitWarningModalButton') exitWarningModalCloseButton: ElementRef;
  @ViewChild('exitWarningModal') exitWarningModal: ElementRef;
  @ViewChild('focAssignActivitiesSuccessModal') focAssignActivitiesSuccessModal: ElementRef;
  @ViewChild('focAssignActivitiesSuccessModalCancel') focAssignActivitiesSuccessModalCancel: ElementRef;
  @ViewChild('openFocAssignActivitiesSuccessModal') openFocAssignActivitiesSuccessModal: ElementRef;
  @ViewChild('assignmentSuccessfulModalButton') assignmentSuccessfulModalButton: ElementRef;
  @ViewChild('dueDateWarningModalButton') dueDateWarningModalButton: ElementRef;
  @ViewChild('assignmentErrorModalButton') assignmentErrorModalButton: ElementRef;
  @ViewChild(GenericErrorModal) public genericErrorModal: GenericErrorModal;

  constructor(
    private route: ActivatedRoute,
    private readonly router: Router,
    private translationService: TranslateService,
    private loaderService: LoaderService,
    private assignmentViewService: AssignmentViewService,
    private appHelperService: AppHelperService,
    private apiGatewayHelperService: ApiGatewayHelperService,
    private messageService: MessageService,
    private productTocService: ProductTocService,
    private htmlMetaManagerService: HtmlMetaManagerService,
    private productViewHelperService: ProductViewHelperService,
    private eventService: EventService,
    private authService: AuthService,
    private readonly contentAccessHelperService: ContentAccessHelperService,
    private contentAccessService: ContentAccessService
  ) { }


  /***********************
   * Lifecycle Hooks
  * **********************/

  ngOnInit(): void {
    this.loaderService.showLoader();
    this.assignmentData = this.assignmentViewService.assignmentCreatedData;
    this.existingAssignmentData = this.assignmentViewService.existingAssignmentData;
    this.currentComponentItemsNotAssigned = this.assignmentViewService.currentComponentItemsNotAssigned;
    this.itemsAlreadyAssignedInClass = this.assignmentViewService.itemsAlreadyAssignedInClass;
    const userData = this.authService.getUserData();
    this.eventService.init({
      userId: userData.extUserId, 
      role: userData.role
    });
    if (!this.assignmentViewService.currentClassLearnersData) {
      this.classLearners = this.assignmentViewService.currentClassLearnersData = this.route.snapshot.data.resolveData.productData.classLearners;
    } else {
      this.classLearners = this.assignmentViewService.currentClassLearnersData;
    }
    this._getRulesConflictItems();
    if (this.route.snapshot.params?.assignmentid && this.router.url?.indexOf("/edit-assignment/") !== -1) {
      this.isEditMode = true;
    }
    /*
    * If there are learners in the class and the assignment is not being updated(from the summary page during the journey creation process) and 
    * and the assignment (existing from the class page) is not being edited:
    * 
    * Then, select all the students by default.
    */
    if (this.classLearners.length && !this.assignmentData.dueDate && !this.isEditMode) {
      this.classLearners.forEach((user) => user.isChecked = true);
    }
    this.classLearners.forEach((user) => {
      if (user.isChecked) {
        this.noOfSelectedLearners++;
      }
    });
    this.paramsSubscription = this.route.params.subscribe((params: Params) => {
      // CLear previous TOC data
      this.spaceId = params.spaceid;
      this.productCode = params.productcode;
      this.classId = params.classid;
      if (this.isEditMode) this.assignmentId = params.assignmentid;
      this.apiGatewayHelperService.setSpaceId(this.spaceId);
      this.productBundleDetails = this.productTocData.bundleDetails;
      this.bundleProductMeta = this.productBundleDetails.products.find(product => product['dls-product'].code === this.productCode);
      this.isTestTypeComponent = this.productViewHelperService.isTestTypeComponent(this.bundleProductMeta);
      this.isMosaicTypeComponent = this.appHelperService.isMosaicProduct(this.productCode);

      // Hide the lock content checkbox for LPv4 products or assignment-only components with rules created through lock/unlock journey
      this.hideLockContentCheckbox =
        this.bundleProductMeta["learning-path"]?.version === "4" ||
        (this.productViewHelperService.getIsAssignmentOnlyProduct() &&
          this.productTocData.productRules?.entities?.some(
            (rule) => !rule.rule_display.assigned_path_id
          ));

      this.classTitle = this.productTocData.classmeta.title;
      this.classStartDate = new Date(this.productTocData.classmeta.class.startdate);
      this.classEndDate = new Date(this.productTocData.classmeta.class.enddate);

      /** If we have due date for an assignment, set the max value of start date to due date for an assignment
       *  otherwise, set the max value of start date to class end date.
       * */
      if (this.assignmentData.dueDate) {
        this.assignmentStartDateMaxValue = new Date(this.assignmentData.dueDate);
      } else {
        this.assignmentStartDateMaxValue = this.classEndDate;
      }

      if (this.minAssignmentStartDate.getTime() < this.productTocData.classmeta.class.startdate) {
        // Assignment due date can't be before class start date
        this.minAssignmentStartDate = new Date(this.productTocData.classmeta.class.startdate);
      }
      //only assign title when its empty or null. Otherwise, the title got override.
      if (!this.assignmentData.title) {
        this.assignmentData.title = `Assignment ${this.assignmentViewService.totalAssignmentCount + 1}, ${this.classTitle}`;
      }

    });

    if (this.isEditMode && !this.assignmentViewService.isExistingAssignmentInitialized) {
      this.currentSectionType = this.SECTION_TYPE.SUMMARY;
      const assignmentEntity = this.productTocData.assignmentData.entities
        .find((entity) => entity.assigned_path_id === this.assignmentId);

      if (assignmentEntity) {
        this.assignmentData.startDate = new Date(assignmentEntity.startdate);
        this.assignmentData.dueDate = this.assignmentStartDateMaxValue = new Date(assignmentEntity.duedate);
        this.assignmentData.created = assignmentEntity.created;
        this.assignmentData.title = assignmentEntity.title || '';

        const lockRule = assignmentEntity.rules?.rule_id && this.productTocData.productRules?.entities.find((rule) => assignmentEntity.rules?.rule_id === rule.ruleid);
        if (lockRule) {
          this.isLockRuleUserScope = lockRule.scope === 'USER';
          this.assignmentData.isContentLocked = true;
          // Show the lock content checkbox if rule created through assignment journey
          this.hideLockContentCheckbox = false;
        }

        this.noOfCheckedItems = assignmentEntity.items?.length || 0;
        if (assignmentEntity.scope === "users") {
          if (this.productTocData.assignedUsers) {
            // Assign the count of users to `this.noOfSelectedLearners`
            this.noOfSelectedLearners = this.productTocData.assignedUsers.length;
            this.classLearners.forEach((learner) => {
              const isLearnerAlreadyEnrolled = this.productTocData.assignedUsers.find((user) => {
                return user.userid === learner.dls_user_id;
              });
              if (isLearnerAlreadyEnrolled) {
                learner.isChecked = true;
              }
            });
          }
        } else {
          // Assignment is global scope, so we need to select all the students by default
          this.noOfSelectedLearners = this.classLearners.length;
          this.classLearners.forEach((user) => user.isChecked = true);
        }
      }

      this.setExisitngAssignmentData(assignmentEntity);
    }
    this.loaderService.hideLoader();

    this.subscriptions.add(this.messageService.getChannelObservable('assignment-detail')
      .subscribe((res: any) => {
        this.redirectData = res.data;
        this.exitWarningModalButton.nativeElement.click();
      }));

    this.productViewHelperService.logOutCalled.subscribe((res) => {
      this.productViewHelperService.saveAlertActionOnLogout.next();
    });
    
    this.subscriptions.add(
      this.assignmentViewService.assignmentCreationState.subscribe((res) => {
        this.isAssignmentEditMode = res.isEditMode;

        // If resetToc OR showSuccessBanner flag is set, show summary section
        if (res.resetToc || res.showSuccessBanner) {
          if (this.isEditMode) this.isExistingAssignmentUpdated();
          this.currentSectionType = this.SECTION_TYPE.SUMMARY;

          if (res.showSuccessBanner && !this.isEditMode) {
            this.showUpdatedContentBanner(this.SECTION_TYPE.CONTENT);
          }
        }
      })
    );
    this.calculateAssignmentDueDateMinValue();
    this.setPageTitleAndDesc();

    $('#exitWarningModal').on('show.bs.modal', () => {
      this.lastActiveElement = <HTMLElement>document.activeElement;
    });

    $('#exitWarningModal').on('shown.bs.modal', () => {
      this.exitWarningModalCloseButton.nativeElement.focus();
      this.exitWarningModalCloseButton.nativeElement.classList.add('focus-visible');
    });

    $('#exitWarningModal').on('hidden.bs.modal', () => {
      this.lastActiveElement?.focus();
    });
  }

  ngOnDestroy() {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
    if (this.paramsSubscription) {
      this.paramsSubscription.unsubscribe();
    }
    if (this.exitWarningModal.nativeElement.classList.contains('show')) {
      this.exitWarningModalCloseButton.nativeElement.click();
    }
    if (this.focAssignActivitiesSuccessModal.nativeElement.classList.contains('show')) {
      this.focAssignActivitiesSuccessModalCancel.nativeElement.click();
    }
  }

  /***********************
   * Functions for interacting with UI
  * **********************/

  public assignNewAssignment() {
    if (this.isCurrentComponentAllItemsAssigned && (this.isTestTypeComponent || this.isMosaicTypeComponent)) {
        const urlOptions = {
          queryParams: { fwd: 'true' }
        };

        if (this.ebookAssignmentFlowParams) {
          urlOptions.queryParams = {
            ...urlOptions.queryParams,
            ...this.ebookAssignmentFlowParams
          }
        }

          return this.appHelperService.navigateToClassApp(['teacher', this.spaceId, 'class', this.classId, 'create', 'assignments'], urlOptions);
    }

    if (this.tocFolderDepth === 0) {
      this.tocArray.forEach(activity => {
        activity.isChecked = false;
        if (this.itemsAlreadyAssigned[activity['item-code']]) {
          if (this.isTestTypeComponent || this.isMosaicTypeComponent) {
            activity.isDisabled = true;
          } else {
            activity.alreadyAssigned = true;
          }
        }
      })
    }
    else {
      this.tocArray.forEach(unit => {
        let unitLessons = [];
        unitLessons = this.productTocService.getLastFolderData(unit);
        unitLessons.forEach(lesson => {
          lesson.checkedItems = 0;
          lesson.itemsDisabled = 0;
          let allItemsAssigned = true;
          if (lesson.items.length === 0) {
            allItemsAssigned = false;
          }
          lesson.items.forEach(item => {
            item.isChecked = false;
            if (!this.itemsAlreadyAssigned[item['item-code']]) {
              allItemsAssigned = false;
            } else if (this.isTestTypeComponent || this.isMosaicTypeComponent) {
              item.isDisabled = true;
              lesson.itemsDisabled++;
            } else {
              item.alreadyAssigned = true;
            }
          });

          lesson.isLessonDisabled = !(this.isTestTypeComponent || this.isMosaicTypeComponent) ? false : allItemsAssigned;
          lesson.showPreviousAssignedText = !(this.isTestTypeComponent  || this.isMosaicTypeComponent) && allItemsAssigned;
        })
      });
    }

    //resetting to default values and emitting the updated values
    this.noOfCheckedItems = 0;
    this.assignmentViewService.resetAssignmentData();
    this.assignmentViewService.updateTotalAssignCountByOne();
    this.noOfCheckedItemsChange.emit(this.noOfCheckedItems);
    this.tocArrayChange.emit(this.tocArray);
    this.productViewHelperService.resetTocSelectionSubject.next(true);
    this.navigateBackToActivities();
  }

  public selectLearner($event, learner) {
    if ($event.currentTarget.checked) {
        learner.isChecked = true;
        this.noOfSelectedLearners++;
    } else {
        learner.isChecked = false;
        this.noOfSelectedLearners--;
    }
    if (this.isAssignmentEditMode) {
      this.isStudentSelectionChanged();
    }
  }

  public selectAllLearners($event) {
    if (!$event.currentTarget.checked || (this.noOfSelectedLearners !== 0 && this.noOfSelectedLearners !== this.classLearners.length)) {
      this.classLearners.forEach(learner => {
        if (learner.isChecked) {
          learner.isChecked = false;
          this.noOfSelectedLearners--;
        }
      });
      $event.target.checked = false;
      $event.target.indeterminate = false;
    } else {
        this.classLearners.forEach(learner => {
          if (!learner.isChecked) {
            learner.isChecked = true;
            this.noOfSelectedLearners++;
          }
        });
        $event.target.checked = true;
        $event.target.indeterminate = false;
    }
    if (this.isAssignmentEditMode) {
      this.isStudentSelectionChanged();
    }
  }

  public navigateToNextPage() {
    if (this.isAssignmentEditMode) {
      if (!this.isEditMode) this.showUpdatedContentBanner(this.currentSectionType);
      this.currentSectionType = this.SECTION_TYPE.SUMMARY;
      this.assignmentViewService.assignmentCreationState.next({
        isAssignDetailCompShown: true,
        isEditMode: false
      });
      if (this.isEditMode) {
        this.isExistingAssignmentUpdated();
      } else {
        this.currentAssignmentData = null;
      }
    } else {
      switch (this.currentSectionType) {
        case this.SECTION_TYPE.DETAILS:
          this.currentSectionType = this.SECTION_TYPE.STUDENT;
          break;
        case this.SECTION_TYPE.STUDENT:
          this.currentSectionType = this.SECTION_TYPE.SUMMARY;
          break;
        default: // SECTION_TYPE.SUMMARY
          if(this.isEditMode) {
            // If the due date is in the past, show the warning modal to edit the due date
            if (this.currentDatetime > (this.assignmentData.dueDate as Date).getTime()) {
              this.dueDateWarningModalButton.nativeElement.click();
            } else {
              this.updateAssignment();
            }
          } else {
            this.createAssignment();
          }
      }
    }
    this.setPageTitleAndDesc();
  }

  public focAssignActivitiesSuccessModalCloseHandler() {
    if (this.canCreateAnotherAssignment) {
      this.assignNewAssignment();
    } else {
      this.navigateToClassReviewAssignmentPage();
    }
  }

  public navigateBackToPrevPage(editCurrentAssignment = false) {
    if (editCurrentAssignment) {
      this.copyCurrentAssignmentData();
      this.assignmentViewService.assignmentCreationState.next({
        isAssignDetailCompShown: true,
        isEditMode: true
      });
    }
    switch(this.currentSectionType) {
      case this.SECTION_TYPE.SUMMARY:
        // Navigate to the class review assignment page when in edit mode, else set current section to STUDENT
        if (this.isEditMode && !editCurrentAssignment) {
          this.navigateToClassReviewAssignmentPage();
        } else {
          this.currentSectionType = this.SECTION_TYPE.STUDENT;
        }
      break;
      case this.SECTION_TYPE.STUDENT:
        this.currentSectionType = this.SECTION_TYPE.DETAILS;
        break;
      default:
        this.currentSectionType = this.SECTION_TYPE.CONTENT;
        this.navigateBackToActivities(editCurrentAssignment);
    }
    this.setPageTitleAndDesc();
  }

  /**
   * Navigates back to the summary page by setting the current section type to SUMMARY,
   * triggers an event to indicate that assignment details are no longer being edited,
   * and resets the assignment data to its original state.
   */
  public goBackToSummaryPage() {
    this.currentSectionType = this.SECTION_TYPE.SUMMARY;
    this.assignmentViewService.assignmentCreationState.next({
      isAssignDetailCompShown: true,
      isEditMode: false
    });
    this.resetTheAssignmentDataToOriginal();
    if (this.isEditMode) this.isExistingAssignmentUpdated();
    this.setPageTitleAndDesc();
  }

  public triggerAssignmentSummaryConfirmationModal() {
    this.exitWarningModalButton.nativeElement.click();
  }

  /**
 * Handles the back button action based on the current state of the component.
 * This function determines the appropriate navigation or modal action 
 * depending on whether the user is editing an existing assignment (`isEditMode`) 
 * or editing an assignment in general (`isAssignmentEditMode`).
 */
  public handleBackButtonAction() {
    // If editing an existing assignment
    if (this.isEditMode) {
      this.isExistingAssignmentUpdated();

      /**
       * If any of the following conditions are met, trigger the confirmation modal:
       * 1. On the details section, if the start date, due date, title, or lock content checkbox has been updated.
       * 2. On the student section, if the student selection has been updated.
       * 3. On the summary section, if any of the data has been updated. 
       */
      if (
        (this.currentSectionType === this.SECTION_TYPE.DETAILS && !this.fromEditWarningModal &&
          (this.isStartDateChanged || this.isDueDateChanged || this.isTitleChanged)) ||
        (this.currentSectionType === this.SECTION_TYPE.STUDENT && this.isStudentSelectionUpdated) ||
        (this.currentSectionType === this.SECTION_TYPE.SUMMARY &&
          (this.isTitleChanged || this.isStudentSelectionUpdated || this.isStartDateChanged || this.isDueDateChanged || this.isAssignmentConentUpdated))
      ) {
        this.isSummaryBackBtnClicked = true;
        this.triggerAssignmentSummaryConfirmationModal();
      } else if (this.currentSectionType === this.SECTION_TYPE.SUMMARY) {
        // Navigate to the Class Review Assignment page
        this.navigateToClassReviewAssignmentPage();
      } else {
        this.goBackToSummaryPage();
      }

      this.fromEditWarningModal = false;
    }
    // If editing an assignment in the assignment creation journey
    else if (this.isAssignmentEditMode) {
      // Navigate back to the summary page
      this.goBackToSummaryPage();
    }
    // Default case: Navigate back to the previous page
    else {
      this.navigateBackToPrevPage();
    }
  }

  /**
   * Handles the click event for the leave button.
   */
  public handleLeaveButtonClick() {
    // Back to summary button is clicked navigate to Summary page else navigate to class review assignment page or Teacher dashboard based on cancel button click.
    if (this.isEditMode && !this.redirectData && this.isAssignmentEditMode && !this.isCancelBtnClicked) {
      this.goBackToSummaryPage();
    } else {
      this.navigateToClassOrDashboardApp();
    }
  }

  /**
   * Navigates to the respective edit assignment page.
   * 
   * @param {any} currentSection - The current section to be edited.
   */
  public navigateToEditAssignmentPage(currentSection) {
    this.currentSectionType = currentSection;
    this.navigateBackToPrevPage(true);
  }

  public onPickerOpen() {
    const pickerContainer = document.getElementsByClassName('owl-dt-container-inner')[0] as HTMLElement;
    if (pickerContainer) {
      pickerContainer.style.margin = '4px';
    }
  }

  /**
   * Displays a banner indicating that the content has been updated.
   * The banner will automatically close after 4 seconds.
   *
   * @param updatedContentSection - The section of the content that has been updated.
   */
  private showUpdatedContentBanner(updatedContentSection) {
    this.updatedSection = updatedContentSection;
    clearTimeout(this.showUpdateBannerTimeout);
    this.showUpdateBannerTimeout = setTimeout(()=>{
      this.closeUpdatedContentBanner();
    }, 4000);
  };

  /**
   * Closes the updated content banner.
   */
  public closeUpdatedContentBanner() {
    this.updatedSection = undefined;
  }

  /**
   * Copies the current assignment data to `currentAssignmentData` property.
   * This method deep clones the `classLearners` array and assigns it to `currentClassLearnersData`.
   * It also copies other properties such as `noOfSelectedLearners`, `dueDate`, `startDate`, `title`, and `created` from `assignmentData`.
   * Additionally, it disables the update button by setting `isUpdateButtonDisabled` to true.
   */
  private copyCurrentAssignmentData() {
    this.currentAssignmentData = {
      currentClassLearnersData: JSON.parse(JSON.stringify(this.classLearners)),
      currentNoOfSelectedLearners: this.noOfSelectedLearners,
      dueDate: this.assignmentData.dueDate,
      startDate: this.assignmentData.startDate,
      title: this.assignmentData.title,
      created: this.assignmentData.created,
      isContentLocked: this.assignmentData.isContentLocked
    };
    this.isUpdateButtonDisabled = true;
  }

  private updateTocDataWithExistingAssignment(items) {
    if(this.tocFolderDepth === 0) {
      this.tocArray = this.tocArray.filter((activity) => {
        if (!activity['contributes-to-completion']) return false;
        if (items.findIndex(item => item['item-code'] === item['item-code']) > -1) {
          if (this.isTestTypeComponent) {
            activity.isDisabled = true;
          } else {
            activity.alreadyAssigned = true;
          }
          this.noOfCheckedItems++;
          activity.isChecked = true;
        }
        return true;
      });
    }
    else {
      this.tocArray.forEach(unit => {
        unit.checkedItems = 0;
        unit.totalItems = 0;
        let unitLessons = [];
        unitLessons = this.productTocService.getLastFolderData(unit);
        unitLessons.forEach(lesson => {
          lesson.checkedItems = 0;
          lesson.itemsDisabled = 0;
          let allItemsAssigned = true;
          if(lesson.items.length === 0) {
            allItemsAssigned = false;
          }
          lesson.items = lesson.items.filter(activity => {
              if (!activity['contributes-to-completion']) return false;
              unit.totalItems++;
              // If a unit/lesson/product is selected then all the items within that are selected.
              if (items.findIndex(item => item['item-code'] === activity['item-code']) > -1) {
                lesson.checkedItems++;
                if (this.tocFolderDepth > 1) unit.checkedItems++;
                activity.isChecked = true;
                if (this.isTestTypeComponent) {
                  activity.isDisabled = true;
                  lesson.itemsDisabled++;
                } else {
                  activity.alreadyAssigned = true;
                }
              } else {
                allItemsAssigned = false;
              }
              return true;
          });
          lesson.isLessonDisabled = !this.isTestTypeComponent ? false : allItemsAssigned;
          lesson.showPreviousAssignedText = !this.isTestTypeComponent && allItemsAssigned;
        })
      })
    }
    this.noOfCheckedItemsChange.emit(this.noOfCheckedItems);
    this.tocArrayChange.emit(this.tocArray);
  }

  /**
   * Resets the assignment data to its original state.
   * 
   * This method restores the assignment data to its initial values, including
   * the due date, start date, title, and creation date. It also resets the 
   * class learners and the number of selected learners to their original states.
   * 
   * @remarks
   * This method also clears the assignment data object after resetting the values.
   */
  private resetTheAssignmentDataToOriginal() {
    this.assignmentData.dueDate = this.currentAssignmentData.dueDate;
    this.assignmentData.startDate = this.currentAssignmentData.startDate;
    this.assignmentData.title = this.currentAssignmentData.title;
    this.assignmentData.created = this.currentAssignmentData.created;
    this.classLearners = this.assignmentViewService.currentClassLearnersData = this.currentAssignmentData.currentClassLearnersData;
    this.noOfSelectedLearners = this.currentAssignmentData.currentNoOfSelectedLearners;
    this.currentAssignmentData = null;
  }

  /**
   * Checks if the student content has changed by comparing the current number of selected learners
   * and their checked status with the previous state. If any changes are detected, the update button
   * is enabled; otherwise, it is disabled.
   */
  private isStudentSelectionChanged() {
    // if no students are selected, disable the update button
    if (!this.noOfSelectedLearners) {
      this.isUpdateButtonDisabled = true;
      return;
    }

    // if count of selected learners has changed, enable the update button
    if (this.currentAssignmentData.currentNoOfSelectedLearners !== this.noOfSelectedLearners) {
      this.isUpdateButtonDisabled = false;
      return;
    }

    // if any student's checked status has changed, enable the update button
    for (let learnerIndex = 0; learnerIndex < this.classLearners.length; learnerIndex++) {
      if ((!!this.currentAssignmentData.currentClassLearnersData[learnerIndex].isChecked) !== (!!this.classLearners[learnerIndex].isChecked)) {
        this.isUpdateButtonDisabled = false;
        return;
      }
    }

    // if no changes are detected, disable the update button
    this.isUpdateButtonDisabled = true;
  }

  /**
   * Checks if the assignment details have changed and updates the state of the update button accordingly.
   *
   * @param detailType - The type of detail that has changed (e.g., 'dueDate', 'startDate', 'title').
   * @param event - The event object containing the new value of the detail.
   */
  public isAssignmentDetailChanged(detailType, event) {
    let value;
    if (event.value) value = event.value;
    else if (event.target && event.target.checked !== undefined) value = event.target.checked;
    else value = event;

    if (!this.assignmentData.title)  {
      this.isUpdateButtonDisabled = true;
      return;
    }

    this.assignmentData[detailType] = value;
    this.calculateAssignmentDueDateMinValue();
    if (
      new Date(this.assignmentData.dueDate).getTime() !== new Date(this.currentAssignmentData.dueDate).getTime() ||
      new Date(this.assignmentData.startDate).getTime() !== new Date(this.currentAssignmentData.startDate).getTime() ||
      this.assignmentData.title !== this.currentAssignmentData.title ||
      this.assignmentData.isContentLocked !== this.currentAssignmentData.isContentLocked
    ) {
      this.isUpdateButtonDisabled = false;
      return;
    }

    this.isUpdateButtonDisabled = true;
  }

  private isExistingAssignmentUpdated() {
    this.isStartDateChanged = this.existingAssignmentData.startDate.getTime() !== this.assignmentData.startDate.getTime();
    this.isDueDateChanged = this.existingAssignmentData.dueDate.getTime() !== new Date(this.assignmentData.dueDate).getTime();
    this.isTitleChanged = this.existingAssignmentData.title !== this.assignmentData.title;
    this.isStudentSelectionUpdated = this.existingAssignmentData.currentNoOfSelectedLearners !== this.noOfSelectedLearners;
    this.isLockContentCheckboxUpdated = this.existingAssignmentData.isContentLocked !== this.assignmentData.isContentLocked;
    if (!this.isStudentSelectionUpdated) {
      // if any student's checked status has changed, show the Edit label for the student section
      for (let learnerIndex = 0; learnerIndex < this.classLearners.length; learnerIndex++) {
        if ((!!this.existingAssignmentData.currentClassLearnersData[learnerIndex].isChecked) !== (!!this.classLearners[learnerIndex].isChecked)) {
          this.isStudentSelectionUpdated = true;
        }
      }
    }

    this.isAssignmentConentUpdated = this.existingAssignmentData.items.length !== this.noOfCheckedItems;
    if (!this.isAssignmentConentUpdated) {
      const getAllLevelsOfTOCData = (item): boolean => {
        if (item.items) {
          return item.items.forEach((subItem) => getAllLevelsOfTOCData(subItem));
        }
  
        const isItemAlreadyAssigned = this.existingAssignmentData.items.findIndex(activity => item['item-code'] === activity['item-code']) > -1;
        if ((item.isChecked && !isItemAlreadyAssigned) || (!item.isChecked && isItemAlreadyAssigned)) {
          this.isAssignmentConentUpdated = true;
        }
      };
      this.tocArray.forEach((unit) => getAllLevelsOfTOCData(unit));
    }
  }

  private setExisitngAssignmentData(data) {
    this.existingAssignmentData.currentClassLearnersData = JSON.parse(JSON.stringify(this.classLearners));
    this.existingAssignmentData.currentNoOfSelectedLearners = this.noOfSelectedLearners;
    this.existingAssignmentData.dueDate = this.assignmentData.dueDate;
    this.existingAssignmentData.startDate = this.assignmentData.startDate;
    this.existingAssignmentData.title = this.assignmentData.title;
    this.existingAssignmentData.created = this.assignmentData.created;
    this.existingAssignmentData.isContentLocked = this.assignmentData.isContentLocked;
    if (data) {
      this.existingAssignmentData.scope = data.scope;
      this.existingAssignmentData.items = data.items;
    }

    if (this.assignmentData.isContentLocked) {
      this.existingAssignmentData.lockRuleId = data?.rules?.rule_id;
    } else {
      this.existingAssignmentData.lockRuleId = null;
    }
  }

  public navigateToClassReviewAssignmentPage() {
    if (this.exitWarningModal.nativeElement.classList.contains('show')) {
      this.exitWarningModalCloseButton.nativeElement.click();
    }
    this.appHelperService.navigateToClassApp(['teacher', this.spaceId, 'class', this.classId, 'view', 'assignments']);
  }

  public navigateToTeacherDashboard() {
    if (this.exitWarningModal.nativeElement.classList.contains('show')) {
      this.exitWarningModalCloseButton.nativeElement.click();
    }
    this.appHelperService.navigateToDashboardApp(['teacher/dashboard']);
  }

  public navigateToClassOrDashboardApp() {
    if (!this.isCancelBtnClicked && !this.isSummaryBackBtnClicked) {
      return this.navigateToTeacherDashboard();
    }
    this.navigateToClassReviewAssignmentPage();
  }

  public navigateToFOCApp() {
    this.appHelperService.navigateToFOC(this.ebookAssignmentFlowParams);
  }

  public navigateToManageAccess() {
    window.open(`/class/teacher/${this.spaceId}/class/${this.classId}/bundle/${this.productTocData.bundleDetails['bundle-code']}/manage-access`, '_blank');
  }

  /**
   * If limit error, navigate to the manage access page.
   * Else retry the action for rule create, update or delete.
   */
  public navigateToAccessRulePage() {
    this.loaderService.showLoader();

    if (this.componentRuleLimitReached || this.rulesDocSizeLimitExceeded) {
      this.appHelperService.navigateToClassApp(['teacher', this.spaceId, 'class', this.classId, 'bundle', this.productTocData.bundleDetails['bundle-code'], 'manage-access']);
    } else {
      this._retryRuleAction();
    }
  }

  public trimSpaces() {
    this.assignmentData.title = this.assignmentData.title.replace(/^\s+/g, '');
    this.isAssignmentDetailChanged('title', this.assignmentData.title);
  }

  public setStartDateMaxVal(event) {
    this.assignmentStartDateMaxValue = event.value;
    this.isAssignmentDetailChanged('dueDate', event);
  }

  public openDatePicker(event: KeyboardEvent, datePicker: OwlDateTimeComponent<any>): void {
    if (event.key === 'Enter') {
      datePicker.open();
    }
  }

  public getDueDateTimestamp() {
    return new Date(this.assignmentData.dueDate).getTime();
  }
  /***********************
   * Private Functions 
   * **********************/

  private _transformAssignmentData() {
    let assignmentTitle: string = this.assignmentData.title;
    let assignmentStartDate: number = this.assignmentData.startDate.getTime();
    let assignmentDueDate: number = new Date(this.assignmentData.dueDate).getTime();

    let data: AssignmentDataParams = {
      title: assignmentTitle,
      duedate: assignmentDueDate,
      startdate: assignmentStartDate,
      shared_progress: true,
      items: [],
      context: {
        'space_key': this.spaceId,
        'productcode': this.productCode,
        'bundle-code': this.productTocData.bundleDetails['bundle-code']
      }
    };

    let newAssignedItems = {};

    this.tocArray.every(unit => {
      let activityItems = [];
      activityItems = this.productTocService.getLeafNodeItems(unit);
      activityItems.every(item => {
        if (item.isChecked) {
          data.items.push({
            'item-code': item['item-code'],
            'dls_product_code': this.productCode,
            'item_type': 'dls-internal'
          });
          newAssignedItems[item['item-code']] = {
            startDate: assignmentStartDate,
            title: assignmentTitle,
            dueDate: assignmentDueDate
          };
          if (item.alreadyAssigned) {
            data.context['re-assigned'] = true;
          }
          this.itemsAlreadyAssignedInClass[this.productCode + '-' + item['item-code']] = true;
          delete this.currentComponentItemsNotAssigned[this.productCode + '-' + item['item-code']];
        }
        return true;
      });
      return true;
    });

    return { data, newAssignedItems };
  }

  private _transformEditAssignmentData(isUserScope) {
    let assignmentData: EditAssignmentDataParams = {
      context: {
        'space_key': this.spaceId,
        'productcode': this.productCode,
        'bundle-code': this.productTocData.bundleDetails['bundle-code']
      },
      ...(this.isTitleChanged && { title: this.assignmentData.title }),
      ...(this.isStartDateChanged && { startdate: this.assignmentData.startDate.getTime() }),
      ...(this.isDueDateChanged && { duedate: new Date(this.assignmentData.dueDate).getTime() }),
      ...(isUserScope && this.existingAssignmentData.scope !== 'users' && { scope: 'users', isSkipNotification: true }),
      ...(!isUserScope && this.existingAssignmentData.scope === 'users' && { scope: 'global', isCustomNotifEventNeeded: this.assignmentData.startDate.getTime() <= Date.now(), startdate: this.assignmentData.startDate.getTime() }),
    };
  
    if (this.isAssignmentConentUpdated) {
      assignmentData.items = [];
      this.tocArray.every(unit => {
        let activityItems = this.productTocService.getLeafNodeItems(unit);
        activityItems.every(item => {
          if (item.isChecked) {
            assignmentData.items.push({
              'item-code': item['item-code'],
              'dls_product_code': this.productCode,
              'item_type': 'dls-internal'
            });
            if (item.alreadyAssigned && this.existingAssignmentData.items?.findIndex(activity => item['item-code'] === activity['item-code']) === -1) {
              assignmentData.context['re-assigned'] = true;
            }
          }
          return true;
        });
        return true;
      });
    }
    return assignmentData;
  }
  /**
   * Get the entities for the rule, based on the selected items in the assignment.
   * @returns Array of entities
   */
  private getContentForRule() {
    let totalItemsInProduct = 0;
    this.tocArray.forEach(unit => {
      let activityItems = this.productTocService.getLeafNodeItems(unit);
      totalItemsInProduct += activityItems.length;
    });

    const entities = [];
    const updateRulesEntity = item => {
        entities.push({
            id: item['item-code'],
            type: item['item-type']
        });
    }
    if (this.noOfCheckedItems === totalItemsInProduct) {
        updateRulesEntity({
            'item-code': this.productCode,
            'item-type': 'product'
        });
    } else {
        if(this.tocFolderDepth === 0) {
            this.tocArray.forEach((item) => {
                if(item.isChecked) {
                    updateRulesEntity(item);
                }
            });
        } 
        else {
            this.tocArray.forEach(unit => {
                if(unit.checkedItems !== 0 && unit.checkedItems === unit.totalItems) {
                    updateRulesEntity(unit);
                } else {
                    const unitLessons = this.productTocService.getLastFolderData(unit);
                    unitLessons.forEach(lesson => {
                        if(lesson.checkedItems !== 0 && lesson.checkedItems === lesson.items.length) {
                            updateRulesEntity(lesson);
                        } else {
                            lesson.items.forEach(item => {
                                if(item.isChecked) {
                                    updateRulesEntity(item);
                                }
                            }) 
                        }
                    });
                }
            });
        }
    }
    return entities;
  }

  /**
   * Get the data for a new lock rule.
   * @param isUserScope - If the assignment is of user scope
   * @param ruleId - id for the lock rule
   * @returns - Object containing the new rule data
   */
  private _getCreateRuleData(isUserScope, ruleId?) {
    return {
      ruleid: ruleId || nanoid(16),
      spaceid: this.spaceId,
      entities: this.getContentForRule(),
      rule_data_common: {
        access: "lock",
        startTime: new Date(this.assignmentData.dueDate).getTime()
      },
      rule_display: {
        ...(this.assignmentId && { assigned_path_id: this.assignmentId }),
      },
      scope: isUserScope ? "USER" : "GLOBAL-ALL-USERS",
    };
  }

  /**
   * Get the access rule params based on the updated assignment data.
   * @param isUserScope - If the assignment is of user scope
   * @param isUserSelectionChanged - If the user selection has changed
   * @returns Access rule params
   */
  private _getAccessRuleParams(isUserScope: boolean, isUserSelectionChanged: number) {
    let accessRuleParams;
    if (this.isLockContentCheckboxUpdated) {
      if (this.assignmentData.isContentLocked) {
        if (this.productTocData?.productRules?.count < this.ruleLimitInComponent){
          // Prepare create rule params if checkbox is checked
          const createRuleData = this._getCreateRuleData(isUserScope);
          if (isUserScope) {
            createRuleData["user"] = this.classLearners.reduce((acc, learner) => {
              if (learner.isChecked) {
                acc.push(learner.userid);
              }
              return acc;
            }, []);
          }

          accessRuleParams = {
            action: 'create',
            productCode: this.productCode,
            data: createRuleData
          }
          this.componentRuleLimitReached = false;
        } else {
          // Show rules limit error modal, after the assignment is updated
          this.componentRuleLimitReached = true;
        }

      } else {
        // Prepare delete rule params if checkbox is unchecked
        accessRuleParams = {
          action: 'delete',
          data: {
            ruleId: this.existingAssignmentData.lockRuleId,
            productCode: this.productCode
          }
        };
      }
    // Check if lock rule is present and needs to be updated
    } else if (
      this.assignmentData.isContentLocked &&
      (isUserSelectionChanged ||
        this.isDueDateChanged ||
        this.isAssignmentConentUpdated)
    ) {
      const isContextChanged = this.isLockRuleUserScope !== isUserScope;

      accessRuleParams = {
        action: "update",
        ruleId: this.existingAssignmentData.lockRuleId,
        productCode: this.productCode
      };

      if (isContextChanged) {
        accessRuleParams.isContextChanged = true;
        accessRuleParams.data = this._getCreateRuleData(isUserScope, this.existingAssignmentData.lockRuleId);
      } else {
        accessRuleParams.data = {
          rule_data_common: {
            access: "lock",
            startTime: new Date(this.assignmentData.dueDate).getTime()
          },
          scope: isUserScope ? "USER" : "GLOBAL-ALL-USERS",
          entities: this.getContentForRule()
        };
      }

      if (isUserScope) {
        accessRuleParams.data.user = this.classLearners.reduce((acc, learner) => {
            if (learner.isChecked) {
              acc.push(learner.userid);
            }
            return acc;
          }, []
        );
      }
    }

    return accessRuleParams;
  }

  private _getRulesConflictItems() {
    const getRulesConflictItemsData = this.contentAccessHelperService.getRulesConflictItems(this.tocArray, this.tocFolderDepth);
    this.unitConflictsMap = getRulesConflictItemsData.unitConflictsMap;
    this.lessonConflictsMap = getRulesConflictItemsData.lessonConflictsMap;
    this.showRulesWarning = getRulesConflictItemsData.showWarning;
  }

  private updateAssignment() {
    this.showLoader = true;
    const isUserScope = (this.noOfSelectedLearners !== 0 && this.noOfSelectedLearners !== this.classLearners.length);
    const isAssignmentMetaChanged = this.hasAssignmentMetaChanged(isUserScope);

    let usersToEnrollInAssignedPath: AssignedLearners[] = [];
    let usersToUnEnrollFromAssignedPath: AssignedLearners[] = [];
    let newNotifyUsers;
    if (!isUserScope && this.productTocData.assignedUsers?.length) {
      newNotifyUsers = { alreadyNotified: true, users: [] };
      /**
       * If the number of newly added users is more than 50,
       * then `alreadyNotified` is set to true → we will send a list of
       * already notified users to the backend (to exclude them from notifications).
       * Otherwise, `alreadyNotified` is false → we will send a list of
       * newly added users to the backend (to notify them).
       */
      newNotifyUsers.alreadyNotified =
        (this.classLearners.length - this.productTocData.assignedUsers.length) > 50;

      usersToUnEnrollFromAssignedPath = this.classLearners.reduce((acc, learner) => {
        const assignedUser = this.productTocData.assignedUsers.find(
          user => user.userid === learner.dls_user_id
        );

        /**
         * Looping over each learner in `classLearners`:
         *
         * 1. If `alreadyNotified` is FALSE → this means we want to send
         *    notifications for learners who are **not** in `assignedUsers`.
         *    - Condition: learner is NOT in `assignedUsers`
         *    - Action: Add them to `newNotifyUsers.users` so backend will notify them.
         *
         * 2. If `alreadyNotified` is TRUE → this means we want to send
         *    a list of learners who are already in `assignedUsers` so backend
         *    can exclude them from new notifications.
         *    - Condition: learner is in `assignedUsers`
         *    - Action: Add them to `newNotifyUsers.users` AND also push them
         *      into `usersToUnEnrollFromAssignedPath` so they can be unenrolled.
         */
        if (!newNotifyUsers.alreadyNotified && !assignedUser) {
          newNotifyUsers.users.push({ extUserId: learner.userid });
        } else if (assignedUser) {
          acc.push({ extUserId: learner.userid });
          if (newNotifyUsers.alreadyNotified) {
            newNotifyUsers.users.push({ extUserId: learner.userid });
          }
        }
        return acc;
      }, []);
    } else if (isUserScope) {
      this.classLearners.forEach((learner) => {
        const isLearnerAlreadyEnrolled = this.productTocData.assignedUsers?.length && this.productTocData.assignedUsers.find((user) => {
          return user.userid === learner.dls_user_id;
        });
        if (learner.isChecked && !isLearnerAlreadyEnrolled) {
          usersToEnrollInAssignedPath.push({ extUserId: learner.userid });
        } else if (!learner.isChecked && isLearnerAlreadyEnrolled) {
          usersToUnEnrollFromAssignedPath.push({ extUserId: learner.userid });
        }
      });
    }


    let assignmentData;

    if (isAssignmentMetaChanged) {
      assignmentData = this._transformEditAssignmentData(isUserScope);
    }

    let accessRuleParams = this._getAccessRuleParams(isUserScope, usersToEnrollInAssignedPath.length || usersToUnEnrollFromAssignedPath.length);

    if (this.componentRuleLimitReached) {
      accessRuleParams = undefined;
    }

    this.rulesDocSizeLimitExceeded = false;
    const ruleAction = accessRuleParams?.action;

    if (accessRuleParams?.action === 'create' || accessRuleParams?.action === 'update') {
      const isRuleSizeValid = this.contentAccessHelperService.isRulesDocSizeValidForLearner(accessRuleParams.data,  this.productTocData, this.productTocData.productRules);
      
      if (!isRuleSizeValid) {
        accessRuleParams = undefined;
        this.rulesDocSizeLimitExceeded = true;
      }
    }

    this.assignmentViewService.updateAssignment(this.spaceId, this.classId, this.assignmentId, usersToEnrollInAssignedPath, usersToUnEnrollFromAssignedPath, assignmentData, accessRuleParams, newNotifyUsers).subscribe((res: any) => {
      if (res.success) {
        if (
          this.componentRuleLimitReached ||
          this.rulesDocSizeLimitExceeded ||
          (accessRuleParams?.action === 'create' && res.createRuleData?.success !== true) ||
          (accessRuleParams?.action === 'update' && res.updateRuleData?.success !== true) ||
          (accessRuleParams?.action === 'delete' && res.deleteRuleData?.success !== true)
        ) {
          this.setErrorModalTranslatedTexts(ruleAction);
          // Set params for retry
          this.ruleParamsForRetry = accessRuleParams;
          this.assignmentErrorModalButton.nativeElement.click();
        } else {
          if (accessRuleParams?.action === 'update') this.isLockRuleUserScope = isUserScope;
          this.assignmentSuccessfulModalButton.nativeElement.click();
        }

        this.setExisitngAssignmentData(res.updatedAssignmentData);

        if (isUserScope) {
          if (!this.productTocData.assignedUsers) this.productTocData.assignedUsers = [];
          // Delete usersToUnEnrollFromAssignedPath users from productTocData.assignedUsers
          if (usersToUnEnrollFromAssignedPath.length) {
            usersToUnEnrollFromAssignedPath.forEach((unEnrollUser) => {
              const unEnrolledlearner = this.classLearners.find((learner) => learner.userid === unEnrollUser.extUserId);
              this.productTocData.assignedUsers = this.productTocData.assignedUsers.filter((user) => {
                return user.userid !== unEnrolledlearner.dls_user_id;
              });
            });
          }
          // Add usersToEnrollInAssignedPath users to productTocData.assignedUsers
          if (usersToEnrollInAssignedPath.length) {
            usersToEnrollInAssignedPath.forEach((enrolledUser) => {
              const enrolledLearner = this.classLearners.find((learner) => learner.userid === enrolledUser.extUserId);
              this.productTocData.assignedUsers.push({ userid: enrolledLearner.dls_user_id });
            });
          }
        } else {
          this.productTocData.assignedUsers = [];
        }
        this.isTitleChanged = this.isStudentSelectionUpdated = this.isStartDateChanged = this.isDueDateChanged = this.isAssignmentConentUpdated = false;
      } else {
        this.genericErrorModal.showGenericErrorModal();
      }
      this.showLoader = false;
    }, _error => {
      this.showLoader = false;
      this.genericErrorModal.showGenericErrorModal();
    });
  }

  /**
 * Determines if the assignment metadata has changed.
 */
  private hasAssignmentMetaChanged(isUserScope): boolean {
    if (this.isTitleChanged || this.isStartDateChanged || this.isDueDateChanged || this.isAssignmentConentUpdated) {
      return true;
    }

    if (this.existingAssignmentData.scope !== 'users' && isUserScope) {
      return true;
    }

    if (this.existingAssignmentData.scope === 'users' && !isUserScope) {
      return true;
    }

    return false;
  }

  private createAssignment() {
    this.showLoader = true;
    let { data, newAssignedItems } = this._transformAssignmentData();

    const isUserScope = this.noOfSelectedLearners !== 0 && this.noOfSelectedLearners !== this.classLearners.length;

    let accessRuleData;
    if (this.assignmentData.isContentLocked) {
      if (this.productTocData?.productRules?.count < this.ruleLimitInComponent){
        accessRuleData = this._getCreateRuleData(isUserScope);
        this.componentRuleLimitReached = false;
      } else {
        this.componentRuleLimitReached = true;
      }
    }

    let assignedLearners: AssignedLearners[] = [];
    if (isUserScope) {
      data['scope'] = 'users';

      if (accessRuleData) accessRuleData["user"] = [];

      assignedLearners = this.classLearners.reduce((acc, learner) => {
        if (learner.isChecked) {
          acc.push({ extUserId: learner.userid });
          if (accessRuleData) accessRuleData["user"].push(learner.userid);
        }
        return acc;
      }, []);
    }

    this.isCurrentComponentAllItemsAssigned = Object.keys(this.currentComponentItemsNotAssigned).length === 0;
    
    /* Count of other assignable components of this bundle */
    let otherAssignableComponentsCount = 0;

    /* Get FlattenedJSON paths for other components of the current bundle, if all the items from this component are assigned */
    let flattenedJSONUrlMap = {};
    if (this.isCurrentComponentAllItemsAssigned) {
      flattenedJSONUrlMap = this.assignmentViewService.getFlattenedJsonUrls(this.productTocData, this.productCode);
      otherAssignableComponentsCount = Object.keys(flattenedJSONUrlMap).length;
    }

    this.rulesDocSizeLimitExceeded = false;
    if (accessRuleData) {
      const isRuleSizeValid = this.contentAccessHelperService.isRulesDocSizeValidForLearner(accessRuleData,  this.productTocData, this.productTocData.productRules);
      
      if (!isRuleSizeValid) {
        accessRuleData = undefined;
        this.rulesDocSizeLimitExceeded = true;
      }
    }


    this.assignmentViewService.createAssignment(this.spaceId, this.classId, data, flattenedJSONUrlMap, assignedLearners, accessRuleData).subscribe((res: any) => {
      if (res.createAssignmentData.assigned_path_id) {
        this.assignmentData.created = true;
        this.assignmentId = res.createAssignmentData.assigned_path_id;

        if (this.ebookAssignmentFlowParams) {
          this.openFocAssignActivitiesSuccessModal.nativeElement.click();
        } else if (this.componentRuleLimitReached || this.rulesDocSizeLimitExceeded || (accessRuleData && res.createRuleData?.success !== true)) {
          this.setErrorModalTranslatedTexts();

          // if res.createRuleData?.success !== true Set params for retry
          this.ruleParamsForRetry = accessRuleData;
          this.assignmentErrorModalButton.nativeElement.click();
        } else {
          this.assignmentSuccessfulModalButton.nativeElement.click();
          if (res.createRuleData?.success === true) {
            accessRuleData.rule_display = {
              assigned_path_id: this.assignmentId
            };

            // Add the new rule to the product's rules list
            this.productTocData.productRules.entities.push(accessRuleData);
            this.productTocData.productRules.count += 1;
          }
        }

        this.eventService.publishEvent(this.eventService.CONSTANTS.FEATURE.ASSIGNMENT_CREATED, {
          organisationId: this.spaceId,
          context: this.ebookAssignmentFlowParams ? 'eReader' : 'class',
          actionType: 'click'
        });

        this.itemsAlreadyAssigned = Object.assign(this.itemsAlreadyAssigned, newAssignedItems);
        if (this.isCurrentComponentAllItemsAssigned && (this.isTestTypeComponent || this.isMosaicTypeComponent)) {
          this.canCreateAnotherAssignment = otherAssignableComponentsCount > 0 && Object.values(this.productTocData.classmeta.bundles).some(bundle =>
            (bundle as any)?.products?.some(product => {
              const productUnits = res.flattenedProductJson[product['dls-product'].code];
              if (!productUnits) return false;

              // If a component in the class is not a Test/Mosaic type i.e. it is a PE, we can create another assignment
              if (!this.productViewHelperService.isTestTypeComponent(product) &&
                !this.appHelperService.isMosaicProduct(product['dls-product'].code)) {
                return true;
              }

              // If a component in the class is a Test/Mosaic and has any unassigned items left, we can create another assignment
              return productUnits.some(unit =>
                this.productTocService.getLeafNodeItems(unit).some(item =>
                  !ContentHelper.isTeacherOnlyContent(item) &&
                  !this.itemsAlreadyAssignedInClass[product['dls-product'].code + '-' + item['item-code']]
                )
              );
            })
          );
        }
      } else {
        this.genericErrorModal.showGenericErrorModal();
      }
      this.showLoader = false;
    }, _error => {
      this.showLoader = false;
      //handling error for assignments limit reached
      if (_error.error.errorCode === 'ASSIGNMENTS_LIMIT_REACHED') {
        this.showAssignmentLimitWarningModal.emit(true);
      } else
        this.genericErrorModal.showGenericErrorModal();
    });
  }

  private setPageTitleAndDesc(){ 
    let pageTitle;
    let pageDesc;
    switch(this.currentSectionType) {
      case this.SECTION_TYPE.SUMMARY:
        if (this.isEditMode) {
        pageTitle = translations.HTML_META.EDIT_ASSIGNMENT.TITLE;
        pageDesc = translations.HTML_META.EDIT_ASSIGNMENT.DESCRIPTION;
        } else {
          pageTitle = translations.HTML_META.ASSIGNMENT_SUMMARY.TITLE;
          pageDesc = translations.HTML_META.ASSIGNMENT_SUMMARY.DESCRIPTION;
        }
        break;
      case this.SECTION_TYPE.STUDENT:
        pageTitle = translations.HTML_META.SELECT_STUDENTS.TITLE;
        pageDesc = translations.HTML_META.SELECT_STUDENTS.DESCRIPTION;
        break;
      case this.SECTION_TYPE.DETAILS:
        pageTitle = translations.HTML_META.ASSIGNMENT_DETAILS.TITLE;
        pageDesc = translations.HTML_META.ASSIGNMENT_DETAILS.DESCRIPTION;
        break;
      case this.SECTION_TYPE.CONTENT:
        if (this.isEditMode) {
          pageTitle = translations.HTML_META.ASSIGNMENT_CONTENT.TITLE + ' - ' + this.bundleProductMeta.meta?.title;
        } else {
          pageTitle = translations.HTML_META.CREATE_ASSIGNMENT.TITLE + ' - ' + this.productBundleDetails?.title;
        }
        pageDesc = translations.HTML_META.ASSIGNMENT_CONTENT.DESCRIPTION;
        break;
    }
    if (pageTitle) {
      this.htmlMetaManagerService.setTitle(translations.HTML_META.LEARNERLEARNINGPATH.TITLE, pageTitle);
      this.htmlMetaManagerService.setDescription(pageDesc);
    }
  }

  private navigateBackToActivities(editMode = false) {
    this.assignmentViewService.assignmentCreationState.next({
      isAssignDetailCompShown: false,
      isEditMode: editMode
    });
  }

  private calculateAssignmentDueDateMinValue() {
    this.assignmentDueDateMinValue = this.currentDatetime > this.assignmentData.startDate.getTime() ? new Date(this.currentDatetime) : this.assignmentData.startDate;
  }

  private _createRule(ruleData) {
    this.contentAccessService.createRule(this.spaceId, this.classId, this.productCode, ruleData, this.classStartDate.getTime()).subscribe((res: any) => {
      this.loaderService.hideLoader();
      if (res.success) {
        this.assignmentSuccessfulModalButton.nativeElement.click();
      } else {
        this.assignmentErrorModalButton.nativeElement.click();
      }
    }, _error => {
      setTimeout(() => {
        this.loaderService.hideLoader();
        this.assignmentErrorModalButton.nativeElement.click();
      }, 500);
    });
  }

  private _updateRule() {
    this.contentAccessService.updateRule(this.spaceId, this.classId, this.productCode, this.ruleParamsForRetry.ruleId, { updateData: this.ruleParamsForRetry.data }, this.classStartDate.getTime(), undefined, this.ruleParamsForRetry.isContextChanged).subscribe((res: any) => {
      this.loaderService.hideLoader();
      if (res.success) {
        this.assignmentSuccessfulModalButton.nativeElement.click();
      } else {
        this.assignmentErrorModalButton.nativeElement.click();
      }
    }, _error => {
      setTimeout(() => {
        this.loaderService.hideLoader();
        this.assignmentErrorModalButton.nativeElement.click();
      }, 500);
    });
  }

  private _deleteRule() {
    this.contentAccessService.deleteRule(this.spaceId, this.classId, this.productCode, this.ruleParamsForRetry.data.ruleId).subscribe((res: any) => {
      this.loaderService.hideLoader();
      if (res.success) {
        this.assignmentSuccessfulModalButton.nativeElement.click();
      } else {
        this.assignmentErrorModalButton.nativeElement.click();
      }
    }, _error => {
      setTimeout(() => {
        this.loaderService.hideLoader();
        this.assignmentErrorModalButton.nativeElement.click();
      }, 500);
    });
  }

  private _retryRuleAction() {
    if (this.ruleParamsForRetry?.action === 'create') {
      this._createRule(this.ruleParamsForRetry.data);
    } else if (this.ruleParamsForRetry?.action === 'update') {
      this._updateRule();
    } else if (this.ruleParamsForRetry?.action === 'delete') {
      this._deleteRule();
    } else {
      this._createRule(this.ruleParamsForRetry);
    }
  };

  /**
   * Sets the translated texts for the error modal based on the error.
   */
  private setErrorModalTranslatedTexts(ruleAction?: string) {
    // Set common error modal texts for create assignment
    this.translationTexts.errorModalHeading = translations.TEACHER.CREATE_ASSIGNMENTS.RULE_CREATION_ERROR;
    this.translationTexts.errorModalMessage2 = translations.TEACHER.CREATE_ASSIGNMENTS.RULE_CREATION_ERROR_MESSAGE_2;
    this.translationTexts.secondaryActionButtonText = translations.TEACHER.CREATE_ASSIGNMENTS.CONTINUE_WITHOUT_LOCK;
    this.translationTexts.primaryActionButtonText = translations.TEACHER.CREATE_ASSIGNMENTS.CREATE_LOCK_RULE;

    if (this.isEditMode) {
      this.translationTexts.errorModalMessage1 = translations.TEACHER.CREATE_ASSIGNMENTS.RULE_EDIT_ERROR_MESSAGE_1;
    } else {
      this.translationTexts.errorModalMessage1 = translations.TEACHER.CREATE_ASSIGNMENTS.RULE_CREATION_ERROR_MESSAGE_1;
    }

    // Set common limit error modal texts
    if (this.componentRuleLimitReached || this.rulesDocSizeLimitExceeded) {
      this.translationTexts.errorModalMessage2 = this.translationService.instant('TEACHER.CREATE_ASSIGNMENTS.RULE_LIMIT_ERROR_MESSAGE_2', { componentName: this.bundleProductMeta.meta?.title });
      this.translationTexts.primaryActionButtonText = translations.TEACHER.CREATE_ASSIGNMENTS.DELETE_EXISTING_RULE;

      if (this.isEditMode) {
        this.translationTexts.errorModalMessage1 = translations.TEACHER.CREATE_ASSIGNMENTS.RULE_LIMIT_EDIT_ERROR_MESSAGE;
      } else {
        this.translationTexts.errorModalMessage1 = translations.TEACHER.CREATE_ASSIGNMENTS.RULE_LIMIT_ERROR_MESSAGE_1;
      }
    } else {
      // If error occurred while updating the lock rule in edit assignment flow
      if (ruleAction === 'update') {
        this.translationTexts.errorModalHeading = translations.TEACHER.CREATE_ASSIGNMENTS.RULE_UPDATE_ERROR;
        this.translationTexts.errorModalMessage1 = translations.TEACHER.CREATE_ASSIGNMENTS.RULE_UPDATE_ERROR_MESSAGE_1;
        this.translationTexts.errorModalMessage2 = translations.TEACHER.CREATE_ASSIGNMENTS.RULE_UPDATE_ERROR_MESSAGE_2;
        this.translationTexts.secondaryActionButtonText = translations.TEACHER.CREATE_ASSIGNMENTS.CONTINUE_WITHOUT_UPDATING;
        this.translationTexts.primaryActionButtonText = translations.TEACHER.CREATE_ASSIGNMENTS.UPDATE_LOCK_RULE;

      // If error occurred while deleting the lock rule in edit assignment flow
      } else if (ruleAction === 'delete') {
        this.translationTexts.errorModalHeading = translations.TEACHER.CREATE_ASSIGNMENTS.RULE_DELETE_ERROR;
        this.translationTexts.errorModalMessage1 = translations.TEACHER.CREATE_ASSIGNMENTS.RULE_DELETE_ERROR_MESSAGE_1;
        this.translationTexts.errorModalMessage2 = translations.TEACHER.CREATE_ASSIGNMENTS.RULE_DELETE_ERROR_MESSAGE_2;
        this.translationTexts.secondaryActionButtonText = translations.TEACHER.CREATE_ASSIGNMENTS.CONTINUE_WITHOUT_REMOVING;
        this.translationTexts.primaryActionButtonText = translations.TEACHER.CREATE_ASSIGNMENTS.DELETE_LOCK_RULE;
      }
    }
    
    // If the component's total rules limit is reached
    if (this.componentRuleLimitReached) {
      this.translationTexts.errorModalHeading = this.translationService.instant('TEACHER.CREATE_ASSIGNMENTS.RULE_LIMIT_ERROR', { limit: this.ruleLimitInComponent, componentName: this.bundleProductMeta.meta?.title });

      // If the component's rules document size limit is exceeded
    } else if (this.rulesDocSizeLimitExceeded) {
      this.translationTexts.errorModalHeading = translations.TEACHER.CREATE_ASSIGNMENTS.RULE_SIZE_ERROR;

      if (ruleAction === 'update') {
        this.translationTexts.errorModalHeading = translations.TEACHER.CREATE_ASSIGNMENTS.RULE_UPDATE_ERROR;
        this.translationTexts.secondaryActionButtonText = translations.TEACHER.CREATE_ASSIGNMENTS.CONTINUE_WITHOUT_UPDATING;
      }
    }
  }
}
